import { Button, Select } from '@forwardflow/ui-v2'
import { useDnDStageMappingContext } from '../../hooks/useDnDStageMappingContext'
import { DnDStageGroupDropZone } from '../DnDStageGroupDropZone'
import { useState } from 'react'
import { ClockIcon, CloseIcon } from '@forwardflow/icons'
import { Modal } from '@mantine/core'

const MONTH_OPTIONS = [
   { value: '0', label: '0' },
   { value: '3', label: '3' },
   { value: '6', label: '6' },
   { value: '9', label: '9' },
   { value: '12', label: '12' },
   { value: '18', label: '18' },
   { value: '24', label: '24' },
   { value: '36', label: '36' },
]

interface NurtureStagesTabProps {
   onRemoveStage?: (stageId: string) => void
   onRemoveTag?: (tagId: string) => void
}

export const NurtureStagesTab = ({
   onRemoveStage,
   onRemoveTag,
}: NurtureStagesTabProps) => {
   const {
      getNurturingGroups,
      getStagesAndTagsByTimeline,
      addNurturingGroup,
      removeNurturingGroup,
      updateNurturingGroupTimeline,
   } = useDnDStageMappingContext()

   const [isModalOpen, setIsModalOpen] = useState(false)
   const [editingTimeline, setEditingTimeline] = useState<string | null>(null)
   const [startMonth, setStartMonth] = useState('')
   const [endMonth, setEndMonth] = useState('')

   const nurturingGroups = getNurturingGroups()

   // Get all existing timelines for duplicate prevention
   const existingTimelines = nurturingGroups.map((g) => g.timeline || '')

   const handleOpenAddModal = () => {
      setStartMonth('')
      setEndMonth('')
      setEditingTimeline(null)
      setIsModalOpen(true)
   }

   const handleSave = () => {
      if (startMonth && endMonth) {
         const timeline = `${startMonth}-${endMonth} Months`

         if (editingTimeline !== null) {
            // Update existing (includes orphaned groups where editingTimeline is '')
            updateNurturingGroupTimeline(editingTimeline, timeline)
         } else {
            // Add new
            addNurturingGroup(timeline)
         }
         handleCloseModal()
      }
   }

   const handleCloseModal = () => {
      setIsModalOpen(false)
      setEditingTimeline(null)
      setStartMonth('')
      setEndMonth('')
   }

   const handleEditStage = (timeline: string) => {
      // Parse timeline string like "0-3 Months" to extract start and end
      const match = timeline.match(/^(\d+)-(\d+)\s*Months?$/i)
      if (match) {
         setStartMonth(match[1])
         setEndMonth(match[2])
      }
      setEditingTimeline(timeline)
      setIsModalOpen(true)
   }

   // Filter end month options to only show values greater than start month
   const getEndMonthOptions = () => {
      if (!startMonth) return MONTH_OPTIONS
      const startValue = parseInt(startMonth)
      return MONTH_OPTIONS.filter((option) => parseInt(option.value) > startValue)
   }

   // Check if a timeline combination already exists (for duplicate prevention)
   const isTimelineDuplicate = (start: string, end: string) => {
      const timeline = `${start}-${end} Months`
      // When editing, allow the current timeline
      if (editingTimeline === timeline) return false
      return existingTimelines.includes(timeline)
   }

   const handleDeleteStage = (timeline: string) => {
      if (
         window.confirm(
            `Are you sure you want to delete the nurturing stage "${timeline}"?`
         )
      ) {
         removeNurturingGroup(timeline)
      }
   }

   return (
      <div className="space-y-5">
         {/* <DnDStageGroupDropZone key={''} id={''} /> */}
         {nurturingGroups.map((group) => {
            const isOrphaned = !group.timeline
            const timeline = group.timeline || ''

            // For orphaned groups, read stages/tags directly since getStagesAndTagsByTimeline
            // can't match groups with undefined timeline
            const { stages, tags } = isOrphaned
               ? { stages: group.stages || [], tags: group.tags || [] }
               : getStagesAndTagsByTimeline(timeline)

            // Create a unique ID by encoding timeline as base64 to avoid parsing issues with hyphens
            const dropZoneId = `ff-nurturing::${timeline}`

            const handleEdit = () => {
               if (isOrphaned) {
                  // Open modal to set timeline for orphaned group
                  setStartMonth('')
                  setEndMonth('')
                  setEditingTimeline('')
                  setIsModalOpen(true)
               } else {
                  handleEditStage(timeline)
               }
            }

            return (
               <DnDStageGroupDropZone
                  isMandatory={group.isMandatory || false}
                  icon={<ClockIcon width={24} height={24} />}
                  key={dropZoneId}
                  id={dropZoneId}
                  groupId="ff-nurturing"
                  stages={stages}
                  tags={tags}
                  title={isOrphaned ? 'No Timeline Set' : timeline}
                  timeline={timeline}
                  onDelete={() => handleDeleteStage(timeline)}
                  onEdit={handleEdit}
                  onClose={onRemoveStage}
                  onCloseTag={onRemoveTag}
               />
            )
         })}

         <Button
            variant="secondary"
            onClick={handleOpenAddModal}
            className="w-full mt-4"
         >
            + Add Nurture Stage
         </Button>

         {nurturingGroups.length === 0 && (
            <div className="text-white/60 text-center py-10">
               No nurturing groups configured
            </div>
         )}

         <Modal
            opened={isModalOpen}
            onClose={handleCloseModal}
            withCloseButton={false}
            centered
            size="md"
            padding={0}
            styles={{
               content: {
                  backgroundColor: 'rgba(0, 0, 0, 0.50)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '12px',
               },
               body: {
                  padding: 0,
               },
            }}
         >
            <div className="flex flex-col p-6">
               {/* Header */}
               <div className="flex items-center justify-between mb-6">
                  <h2 className="text-white text-lg font-medium">
                     {editingTimeline === null
                        ? 'Add Nurture Stage'
                        : editingTimeline === ''
                          ? 'Set Timeline'
                          : 'Edit Timeline'}
                  </h2>
                  <button
                     onClick={handleCloseModal}
                     className="text-white/70 hover:text-white transition-colors p-1"
                  >
                     <CloseIcon className="w-4 h-4" />
                  </button>
               </div>

               {/* Content */}
               <div className="mb-6">
                  <label className="block text-white/90 text-sm mb-3">
                     Select Timeline Range
                  </label>
                  <div className="flex items-center gap-3">
                     <Select
                        className="flex-1"
                        placeholder="From"
                        value={startMonth}
                        onChange={(value) => {
                           setStartMonth(value || '')
                           // Reset end month if it's no longer valid
                           if (endMonth && value && parseInt(value) >= parseInt(endMonth)) {
                              setEndMonth('')
                           }
                        }}
                        data={MONTH_OPTIONS}
                     />
                     <span className="text-white/60">to</span>
                     <Select
                        className="flex-1"
                        placeholder="To"
                        value={endMonth}
                        onChange={(value) => setEndMonth(value || '')}
                        data={getEndMonthOptions()}
                        disabled={!startMonth}
                     />
                     <span className="text-white/60">Months</span>
                  </div>
                  {startMonth && endMonth && isTimelineDuplicate(startMonth, endMonth) && (
                     <p className="text-red-400 text-sm mt-2">
                        This timeline range already exists
                     </p>
                  )}
               </div>

               {/* Actions */}
               <div className="flex gap-3">
                  <Button
                     variant="secondary"
                     onClick={handleCloseModal}
                     className="flex-1"
                  >
                     Cancel
                  </Button>
                  <Button
                     variant="primary"
                     onClick={handleSave}
                     className="flex-1"
                     disabled={!startMonth || !endMonth || isTimelineDuplicate(startMonth, endMonth)}
                  >
                     {editingTimeline !== null ? 'Save' : 'Add'}
                  </Button>
               </div>
            </div>
         </Modal>
      </div>
   )
}
